import { useState } from 'react';
import { sendMagicLink } from '@/lib/auth';
import { OTP_DIGITS } from '@/lib/otp';
import { PLATFORM_DOMAIN } from '@/lib/hosts';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '@/lib/brand';
import { TeneanLockup } from '@/components/TeneanLockup';
import { isCell } from '@/lib/cell/config';
import { signInWithPassword } from '@/lib/cell/cognito';

export default function Login() {
  // CELL (M28): Tenean's Cognito sign-in. Email codes come with the email rail (M18); until then, a password.
  if (isCell()) return <CellLogin />;
  return <SupabaseLogin />;
}

function CellLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await signInWithPassword(email, password); window.location.assign('/'); }
    catch (err) { setError((err as Error).message); setBusy(false); }
  }
  return (
    <div className="min-h-screen bg-brand-fog flex flex-col">
      <header className="px-6 pt-12 pb-8 text-center">
        <h1 className="flex justify-center" aria-label={PRODUCT_NAME}><TeneanLockup width={150} /></h1>
        <p className="text-[12px] text-image-charcoal mt-2">{PRODUCT_TAGLINE}</p>
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-image-slate mt-2">Pro Hub</p>
        <p className="text-sm text-brand-slate mt-1">Your studio, in your pocket.</p>
      </header>
      <main className="flex-1 px-6 max-w-md w-full mx-auto">
        <form onSubmit={onSubmit} className="card space-y-4">
          <div>
            <label htmlFor="email" className="label">Email</label>
            <input id="email" type="email" autoComplete="username" className="input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label htmlFor="password" className="label">Password</label>
            <input id="password" type="password" autoComplete="current-password" className="input mt-1" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={busy || !email.trim() || !password}>{busy ? 'Signing in…' : 'Sign in'}</button>
          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
        </form>
      </main>
    </div>
  );
}

function SupabaseLogin() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  /**
   * THE CODE PATH (PROHUB-49; Peter's real phone, 2026-08-29: "magic error").
   * The magic link is one-shot and mail apps prefetch links — Apple's scanning can burn
   * the link before the tap, and a PKCE link opened outside the requesting browser can
   * never complete. The SAME email already carries a code (the iOS app's path); typing it
   * here signs in via verifyOtp — no link, nothing for a scanner to consume.
   *
   * IT IS EIGHT DIGITS, NOT SIX (PROHUB-77, feedback 002a5902). Phil: "the sign-in email
   * said it was six digits, but it's actually eight." Verified against the live project
   * rather than the repo, because the two disagree: the Supabase management API reports
   * `mailer_otp_length = 8` for dyoqjqhkfxpabgzbtetw (read 2026-09-10), while
   * studios-backend's config.toml still declared 6 — fixed there in the same sweep, since
   * a deploy that applied that file would have SHORTENED the code without anybody asking.
   *
   * This copy mattered more than a typo usually does: the code path is the ANSWER to the
   * other half of Phil's report — a link opened in whichever browser the platform prefers
   * (Edge, Chrome) cannot complete a PKCE sign-in, and this is the way through. Telling
   * somebody to look for six digits in an eight-digit code is telling them the escape
   * hatch is broken.
   *
   * AND THE BOX HAD TO FOLLOW THE WORDS (feedback acfbbf46, four days later). PROHUB-77 fixed
   * every sentence and left the INPUT at six — maxLength, the placeholder and the button's
   * enable-length were all still 6 — so the page said "type the 8-digit code" over a box that
   * refused the seventh digit. The words were verified and the control was assumed. Every one of
   * those now reads OTP_DIGITS, and the guard reads the input's attributes as well as the prose.
   */
  const [code, setCode] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeErr, setCodeErr] = useState<string | null>(null);

  async function onCode(e: React.FormEvent) {
    e.preventDefault();
    const token = code.replace(/\D/g, '');
    // A minimum, not an equality: the length is a server setting we do not own, and a check that
    // insists on exactly today's value is the thing that breaks silently the day it changes.
    if (token.length < OTP_DIGITS) { setCodeErr(`The code is the ${OTP_DIGITS} digits from the email.`); return; }
    setCodeBusy(true); setCodeErr(null);
    // Lazy import: the eager client needs env at module load, which the test harness lacks.
    const { supabase } = await import('@/lib/supabase');
    // CELL (M28): production only — the cell build renders CellLogin instead of this form.
    const { error: err } = await supabase.auth.verifyOtp({ email: email.trim(), token, type: 'email' });
    setCodeBusy(false);
    if (err) {
      setCodeErr(/expired|invalid/i.test(err.message)
        ? 'That code did not match — check the newest email, codes expire after an hour.'
        : err.message);
      return;
    }
    window.location.assign('/');
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    setError(null);
    const { error: err } = await sendMagicLink(email);
    if (err) {
      setError(err);
      setStatus('error');
    } else {
      setStatus('sent');
    }
  }

  return (
    <div className="min-h-screen bg-brand-fog flex flex-col">
      <header className="px-6 pt-12 pb-8 text-center">
        {/* Matches the Owner Hub sign-in card's type (image-os-web: .signcard
            .brand 21px; .tsub 12px charcoal, sentence case). */}
        <h1 className="flex justify-center" aria-label={PRODUCT_NAME}><TeneanLockup width={150} /></h1>
        <p className="text-[12px] text-image-charcoal mt-2">{PRODUCT_TAGLINE}</p>
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-image-slate mt-2">Pro Hub</p>
        <p className="text-sm text-brand-slate mt-1">Your studio, in your pocket.</p>
      </header>

      <main className="flex-1 px-6 max-w-md w-full mx-auto">
        {/* The first-time-tester banner is GONE (Peter, 2026-08-29, seeing the login on a real
            phone as real pros arrive: "link says first time testing, they are not testing").
            The tester loop's plan stays reachable at /test/ for anyone sent there directly;
            the front door greets pros, not testers. */}
        <div className="card">
          {status === 'sent' ? (
            <div className="text-center py-4">
              <div className="text-4xl mb-3" aria-hidden>✉️</div>
              <h2 className="font-display text-xl text-brand-deep mb-2">Check your inbox</h2>
              <p className="text-sm text-brand-slate">
                We just sent a sign-in link to <strong>{email}</strong>. Tap
                the link on this device — or type the {OTP_DIGITS}-digit code from the
                same email below.
              </p>
              <form onSubmit={onCode} className="mt-4 space-y-2 text-left">
                <label htmlFor="otp" className="label">{OTP_DIGITS}-digit code</label>
                <input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={OTP_DIGITS}
                  className="input mt-1 text-center tracking-[0.4em] text-lg"
                  placeholder={'1234567890'.slice(0, OTP_DIGITS)}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-otp-input
                />
                <button type="submit" className="btn-primary w-full" disabled={codeBusy || code.replace(/\D/g, '').length < OTP_DIGITS}>
                  {codeBusy ? 'Checking…' : 'Sign in with the code'}
                </button>
                {codeErr && <p className="text-xs text-red-600" role="alert">{codeErr}</p>}
              </form>
              <button
                onClick={() => { setStatus('idle'); setEmail(''); setCode(''); setCodeErr(null); }}
                className="btn-ghost mt-6 text-sm"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="label">Email</label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="input mt-1"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Send magic link'}
              </button>
              {status === 'error' && error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              <p className="text-xs text-brand-slate text-center pt-2">
                No password to remember. We'll email you a one-click sign-in
                link.
              </p>
            </form>
          )}
        </div>
      </main>

      <footer className="text-center text-xs text-brand-slate py-6">
        {PRODUCT_NAME} · {PLATFORM_DOMAIN}
        <span className="block mt-1 text-brand-slate/70">© 2026 Cadence Advisers</span>
      </footer>
    </div>
  );
}
